import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { AU } from '../config';
import type { BodyState, Universe } from '../astro/Universe';
import { createSeededRandom } from '../utils/random';
import { markForeground } from './foreground';

// Deterministic asteroid fields. Every client generates the same rocks from the same seeds, so only
// destruction events need to be networked.

export type Rock = {
  id: string;
  anchor: 'earthsun' | 'sun';
  local: THREE.Vector3; // position in the anchor frame (m)
  radius: number;
  variant: number;
  axis: THREE.Vector3;
  spin: number; // rad/s
  hp: number;
  maxHp: number;
  world: THREE.Vector3; // heliocentric, refreshed each frame
};

export const ASTEROIDS = {
  sectorSize: 40_000,
  beltInner: 2.1 * AU,
  beltOuter: 3.3 * AU,
  rocksPerSector: 18,
  respawnMs: 5 * 60_000,
  pointsPerRock: 10,
  lod0Distance: 8_000,
  drawDistance: 90_000,
};

/** Earth–Sun frame: x → Sun, z → ecliptic north. Rotates ~1°/day, so it is effectively fixed per session. */
export function earthSunBasis(universe: Universe, out: THREE.Matrix4): THREE.Matrix4 {
  const earth = universe.get('earth');
  const x = universe.get('sun').position.clone().sub(earth.position).normalize();
  const z = new THREE.Vector3(0, 0, 1);
  const y = new THREE.Vector3().crossVectors(z, x).normalize();
  z.crossVectors(x, y);
  return out.makeBasis(x, y, z);
}

/** Shared spawn point: 400 km over the day side, 40° from the sub-solar point. */
export function spawnLocal(out: THREE.Vector3, radius: number, altitude: number): THREE.Vector3 {
  const a = (40 * Math.PI) / 180;
  return out.set(Math.cos(a), Math.sin(a), 0).multiplyScalar(radius + altitude);
}

const tmpM = new THREE.Matrix4();
const tmpV = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const tmpS = new THREE.Vector3();
const tmpMat = new THREE.Matrix4();

type VariantMesh = { near: THREE.InstancedMesh; geometryRadius: number };

export class Asteroids {
  readonly group = new THREE.Group();
  private readonly rocks = new Map<string, Rock>();
  private readonly sectors = new Map<string, Rock[]>();
  private readonly destroyed = new Map<string, number>(); // id -> time destroyed (server ms)
  private variants: VariantMesh[] = [];
  private far?: THREE.InstancedMesh;
  private readonly driftRocks: Rock[] = [];
  readonly active: Rock[] = [];
  private readonly esBasis = new THREE.Matrix4();
  private ready = false;
  private time = 0;

  constructor(private readonly universe: Universe) {
    this.buildDrift();
  }

  async load(): Promise<void> {
    const loader = new GLTFLoader();
    const names = ['moon_rock_01', 'moon_rock_02', 'moon_rock_03'];
    const gltfs = await Promise.all(names.map((n) => loader.loadAsync(`assets/models/${n}/${n}.gltf`)));
    const MAX = 400;
    this.variants = gltfs.map((g) => {
      let mesh: THREE.Mesh | null = null;
      g.scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh && (!mesh || m.name.includes('LOD0'))) mesh = m;
      });
      const src = mesh as unknown as THREE.Mesh;
      const geo = src.geometry.clone();
      geo.computeBoundingSphere();
      const bs = geo.boundingSphere!;
      geo.translate(-bs.center.x, -bs.center.y, -bs.center.z);
      geo.scale(1 / bs.radius, 1 / bs.radius, 1 / bs.radius);
      const mat = (src.material as THREE.MeshStandardMaterial).clone();
      mat.roughness = 1;
      mat.envMapIntensity = 0.3;
      const near = new THREE.InstancedMesh(geo, mat, MAX);
      near.count = 0;
      near.frustumCulled = false;
      markForeground(near);
      this.group.add(near);
      return { near, geometryRadius: 1 };
    });
    // Far impostor: the lowest LOD of rock 01.
    let lod3: THREE.Mesh | null = null;
    gltfs[0].scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && m.name.includes('LOD3')) lod3 = m;
    });
    const farSrc = (lod3 ?? this.variants[0].near) as THREE.Mesh;
    const farGeo = farSrc.geometry.clone();
    farGeo.computeBoundingSphere();
    const fbs = farGeo.boundingSphere!;
    farGeo.translate(-fbs.center.x, -fbs.center.y, -fbs.center.z);
    farGeo.scale(1 / fbs.radius, 1 / fbs.radius, 1 / fbs.radius);
    this.far = new THREE.InstancedMesh(farGeo, (this.variants[0].near.material as THREE.Material).clone(), 1200);
    this.far.count = 0;
    this.far.frustumCulled = false;
    markForeground(this.far);
    this.group.add(this.far);
    this.ready = true;
  }

  private makeRock(id: string, anchor: Rock['anchor'], local: THREE.Vector3, rng: () => number, big: boolean): Rock {
    const radius = big ? 450 + rng() * 1050 : 12 + Math.pow(rng(), 2.2) * 210;
    const maxHp = Math.min(10, 1 + Math.ceil(radius / 45));
    return {
      id, anchor, local, radius,
      variant: Math.floor(rng() * 3),
      axis: new THREE.Vector3(rng() - 0.5, rng() - 0.5, rng() - 0.5).normalize(),
      spin: (rng() - 0.5) * (big ? 0.02 : 0.25),
      hp: maxHp, maxHp,
      world: new THREE.Vector3(),
    };
  }

  /** "The Drift": a shared cluster just ahead of the spawn point. */
  private buildDrift(): void {
    const rng = createSeededRandom(4242);
    const earthR = 6_378_137;
    const spawn = spawnLocal(new THREE.Vector3(), earthR, 400_000);
    // Orbit direction at the spawn point (prograde, perpendicular to up in the ecliptic plane).
    const up = spawn.clone().normalize();
    const along = new THREE.Vector3(0, 0, 1).cross(up).normalize();
    // Above and slightly ahead of the spawn: visible at once, but the prograde path stays clear for warp.
    const center = spawn.clone().addScaledVector(up, 12_500).addScaledVector(along, 4_000);
    for (let i = 0; i < 180; i++) {
      const r = 8_000 * Math.cbrt(rng());
      const dir = new THREE.Vector3(rng() * 2 - 1, rng() * 2 - 1, (rng() * 2 - 1) * 0.5).normalize();
      const local = center.clone().addScaledVector(dir, r);
      const rock = this.makeRock(`drift:${i}`, 'earthsun', local, rng, i < 3);
      this.driftRocks.push(rock);
      this.rocks.set(rock.id, rock);
    }
  }

  private beltDensity(p: THREE.Vector3): number {
    const r = Math.hypot(p.x, p.y);
    if (r < ASTEROIDS.beltInner || r > ASTEROIDS.beltOuter) return 0;
    const z = Math.abs(p.z) / r;
    if (z > 0.16) return 0;
    const edge = Math.min(r - ASTEROIDS.beltInner, ASTEROIDS.beltOuter - r) / (0.2 * AU);
    return Math.min(1, edge) * (1 - z / 0.16);
  }

  private sector(ix: number, iy: number, iz: number): Rock[] {
    const key = `${ix}:${iy}:${iz}`;
    let list = this.sectors.get(key);
    if (list) return list;
    list = [];
    const S = ASTEROIDS.sectorSize;
    const c = new THREE.Vector3((ix + 0.5) * S, (iy + 0.5) * S, (iz + 0.5) * S);
    const density = this.beltDensity(c);
    if (density > 0) {
      const seed = (Math.imul(ix, 73856093) ^ Math.imul(iy, 19349663) ^ Math.imul(iz, 83492791)) >>> 0;
      const rng = createSeededRandom(seed);
      const n = Math.round(ASTEROIDS.rocksPerSector * density * (0.5 + rng()));
      for (let i = 0; i < n; i++) {
        const local = new THREE.Vector3(ix + rng(), iy + rng(), iz + rng()).multiplyScalar(S);
        const rock = this.makeRock(`belt:${key}:${i}`, 'sun', local, rng, rng() < 0.04);
        list.push(rock);
        this.rocks.set(rock.id, rock);
      }
    }
    this.sectors.set(key, list);
    if (this.sectors.size > 400) this.pruneSectors(c);
    return list;
  }

  private pruneSectors(near: THREE.Vector3): void {
    const S = ASTEROIDS.sectorSize;
    for (const [key, list] of this.sectors) {
      const [x, y, z] = key.split(':').map(Number);
      if (Math.abs((x + 0.5) * S - near.x) + Math.abs((y + 0.5) * S - near.y) + Math.abs((z + 0.5) * S - near.z) > 8 * S) {
        for (const r of list) this.rocks.delete(r.id);
        this.sectors.delete(key);
      }
    }
  }

  inBelt(pos: THREE.Vector3): boolean {
    return this.beltDensity(pos) > 0;
  }

  get(id: string): Rock | undefined {
    return this.rocks.get(id);
  }

  isDestroyed(id: string, nowMs: number): boolean {
    const t = this.destroyed.get(id);
    if (t === undefined) return false;
    if (nowMs - t > ASTEROIDS.respawnMs) {
      this.destroyed.delete(id);
      const r = this.rocks.get(id);
      if (r) r.hp = r.maxHp;
      return false;
    }
    return true;
  }

  markDestroyed(id: string, atMs: number): void {
    this.destroyed.set(id, atMs);
  }

  /**
   * Collect rocks near the ship and refresh their world positions. Call right after the ephemeris
   * update, before anything tests against rocks (Earth moves ~500 m per frame).
   */
  refresh(dt: number, nowMs: number, shipPos: THREE.Vector3): void {
    this.time += dt;
    this.active.length = 0;
    const origin = shipPos;
    earthSunBasis(this.universe, this.esBasis);
    const earth = this.universe.get('earth');

    const consider = (r: Rock) => {
      if (this.isDestroyed(r.id, nowMs)) return;
      if (r.anchor === 'earthsun') r.world.copy(r.local).applyMatrix4(this.esBasis).add(earth.position);
      else r.world.copy(r.local);
      if (r.world.distanceTo(origin) - r.radius < ASTEROIDS.drawDistance) this.active.push(r);
    };

    if (earth.position.distanceTo(shipPos) - earth.def.radius < 2_000_000) this.driftRocks.forEach(consider);
    if (this.inBelt(shipPos)) {
      const S = ASTEROIDS.sectorSize;
      const cx = Math.floor(shipPos.x / S), cy = Math.floor(shipPos.y / S), cz = Math.floor(shipPos.z / S);
      for (let dx = -2; dx <= 2; dx++)
        for (let dy = -2; dy <= 2; dy++)
          for (let dz = -1; dz <= 1; dz++) this.sector(cx + dx, cy + dy, cz + dz).forEach(consider);
    }
  }

  /** Write instance matrices relative to the camera origin. */
  draw(origin: THREE.Vector3): void {
    if (!this.ready) return;
    const counts = this.variants.map(() => 0);
    let farCount = 0;
    for (const r of this.active) {
      tmpV.subVectors(r.world, origin);
      const dist = tmpV.length();
      tmpQ.setFromAxisAngle(r.axis, r.spin * this.time);
      tmpS.setScalar(r.radius);
      tmpMat.compose(tmpV, tmpQ, tmpS);
      if (dist < ASTEROIDS.lod0Distance + r.radius * 10) {
        const v = this.variants[r.variant];
        if (counts[r.variant] < v.near.instanceMatrix.count) v.near.setMatrixAt(counts[r.variant]++, tmpMat);
      } else if (this.far && farCount < this.far.instanceMatrix.count) {
        this.far.setMatrixAt(farCount++, tmpMat);
      }
    }
    this.variants.forEach((v, i) => {
      v.near.count = counts[i];
      v.near.instanceMatrix.needsUpdate = true;
    });
    if (this.far) {
      this.far.count = farCount;
      this.far.instanceMatrix.needsUpdate = true;
    }
  }

  /** First rock hit by a ray (world, heliocentric). */
  raycast(from: THREE.Vector3, dir: THREE.Vector3, range: number): { rock: Rock; distance: number } | null {
    let best: { rock: Rock; distance: number } | null = null;
    for (const r of this.active) {
      const oc = tmpV.subVectors(r.world, from);
      const t = oc.dot(dir);
      if (t < 0 || t > range + r.radius) continue;
      const d2 = oc.lengthSq() - t * t;
      const rr = r.radius * 0.9;
      if (d2 > rr * rr) continue;
      const hit = t - Math.sqrt(rr * rr - d2);
      if (!best || hit < best.distance) best = { rock: r, distance: Math.max(0, hit) };
    }
    return best;
  }

  /** Nearest rock inside a cone around dir (aim assist). */
  aimAssist(from: THREE.Vector3, dir: THREE.Vector3, range: number, coneRad: number): Rock | null {
    let best: Rock | null = null;
    let bestScore = Infinity;
    const cos = Math.cos(coneRad);
    for (const r of this.active) {
      const oc = tmpV.subVectors(r.world, from);
      const dist = oc.length();
      if (dist > range + r.radius || dist < 1) continue;
      const c = oc.dot(dir) / dist;
      if (c < cos) continue;
      const score = dist * (2 - c);
      if (score < bestScore) {
        bestScore = score;
        best = r;
      }
    }
    return best;
  }

  /** Distance from a point to the nearest active rock surface. */
  nearestSurface(p: THREE.Vector3): number {
    let best = Infinity;
    for (const r of this.active) best = Math.min(best, r.world.distanceTo(p) - r.radius);
    return best;
  }

  /** First rock overlapping a capsule (segment a→b, radius). */
  overlappingCapsule(a: THREE.Vector3, b: THREE.Vector3, radius: number): { rock: Rock; closest: THREE.Vector3 } | null {
    const ab = new THREE.Vector3().subVectors(b, a);
    const len2 = ab.lengthSq();
    for (const r of this.active) {
      const t = THREE.MathUtils.clamp(new THREE.Vector3().subVectors(r.world, a).dot(ab) / len2, 0, 1);
      const closest = a.clone().addScaledVector(ab, t);
      const rr = r.radius * 0.8 + radius;
      if (closest.distanceToSquared(r.world) < rr * rr) return { rock: r, closest };
    }
    return null;
  }

  /** Rocks overlapping a sphere (ship collision). */
  overlapping(center: THREE.Vector3, radius: number): Rock | null {
    for (const r of this.active) {
      const rr = r.radius * 0.8 + radius;
      if (r.world.distanceToSquared(center) < rr * rr) return r;
    }
    return null;
  }

  frameOf(rock: Rock): BodyState {
    return rock.anchor === 'earthsun' ? this.universe.get('earth') : this.universe.get('sun');
  }

  static readonly tmp = tmpM;
}
